/**
 * FAQSection.tsx
 * ───────────────
 * Section FAQ home — 2 colonnes :
 * - Gauche  : titre + 3 questions accordéon
 * - Droite  : 2 questions + encart contact bleu
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Textes : faq.items.<id>.q / .a
const FAQS_LEFT  = ['audience', 'free_tier', 'unlimited'];
const FAQS_RIGHT = ['verified', 'why_not_chatgpt'];

const FaqItem = ({ q, a, isOpen, onToggle }: { q: string; a: string; isOpen: boolean; onToggle: () => void }) => (
  <div className={`rounded-xl border bg-card overflow-hidden transition-colors ${isOpen ? 'border-primary/30' : 'border-border hover:border-primary/20'}`}>
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between gap-4 px-5 py-[18px] text-left text-[14px] font-semibold text-foreground hover:bg-accent transition-colors"
    >
      <span>{q}</span>
      <span className={`w-[22px] h-[22px] flex items-center justify-center flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180 text-primary' : 'text-muted-foreground'}`}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </span>
    </button>
    {isOpen && (
      <div className="px-5 pb-[18px] pt-3 text-[13px] text-muted-foreground leading-[1.7] border-t border-border">
        {a}
      </div>
    )}
  </div>
);

export const FAQSection = () => {
  const { t } = useTranslation();
  const [openIdx, setOpenIdx] = useState<number>(0); // 0 = première ouverte par défaut

  const toggle = (i: number) => setOpenIdx(prev => prev === i ? -1 : i);

  return (
  <section className="py-20 bg-background border-t border-border">
    <div className="max-w-[1100px] mx-auto px-6 sm:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">

        {/* ── Colonne gauche ── */}
        <div>
          <span className="block text-[10px] font-bold uppercase tracking-[.14em] text-primary mb-2.5">
            {t('faq.eyebrow')}
          </span>
          <h2 className="font-serif text-[30px] sm:text-[34px] font-bold text-foreground tracking-[-0.03em] leading-tight mb-8">
            {t('faq.title_start')}<br />
            <em className="not-italic text-primary">{t('faq.title_highlight')}</em>
          </h2>
          <div className="flex flex-col gap-2.5">
            {FAQS_LEFT.map((id, i) => (
              <FaqItem key={id} q={t(`faq.items.${id}.q`)} a={t(`faq.items.${id}.a`)} isOpen={openIdx === i} onToggle={() => toggle(i)} />
            ))}
          </div>
        </div>

        {/* ── Colonne droite ── */}
        <div className="flex flex-col gap-2.5 lg:pt-[76px]">
          {FAQS_RIGHT.map((id, i) => (
            <FaqItem key={id} q={t(`faq.items.${id}.q`)} a={t(`faq.items.${id}.a`)} isOpen={openIdx === FAQS_LEFT.length + i} onToggle={() => toggle(FAQS_LEFT.length + i)} />
          ))}

          {/* Encart contact */}
          <div className="rounded-xl border border-primary/20 bg-primary/5 px-5 py-[18px] mt-0.5">
            <p className="text-[13px] font-semibold text-foreground mb-1.5">
              {t('faq.other_question')}
            </p>
            <p className="text-[12px] text-muted-foreground leading-relaxed mb-3">
              {t('faq.other_question_desc')}
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-[12px] font-semibold hover:bg-primary/90 transition-colors"
            >
              {t('faq.contact_us')}
            </Link>
          </div>

          {/* Lien FAQ complète */}
          <div className="flex justify-end pt-1">
            <Link
              to="/faq"
              className="text-[12px] text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
            >
              {t('faq.all_questions')}
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
              </svg>
            </Link>
          </div>
        </div>

      </div>
    </div>
  </section>
  );
};